import { AUTH_CATEGORIES } from '../components/Auth';

// #region JSDoc Types

/**
 * @typedef {import('../components/Auth').UserCategory} UserCategory
 */

/**
 * @typedef {object} PageTrackingConfig
 * @property {string | ((userCategory: UserCategory) => string)} pageName - Page name for analytics (e.g., "Checkout", "Landing Page") or function that returns pageName based on user category
 */

/**
 * @typedef {object} RouteDefinition
 * @property {string} path - Route path used by React Router
 * @property {PageTrackingConfig} tracking - Page tracking metadata for Tealium/Cohesion
 * @property {UserCategory[]} [auth] - If present, route requires authentication with these allowed user categories
 */

// #endregion

/**
 * @type {Record<string, RouteDefinition>}
 */
export const ROUTES = Object.freeze({
  LANDING_PAGE: {
    path: '/',
    tracking: {
      pageName: 'Elevated Landing',
    },
    // No auth - public page (but has conditional rendering via LandingPageGuard)
  },
  ADD_PROVIDERS_TO_ROSTER_PAGE: {
    path: '/add-providers-to-roster',
    tracking: {
      pageName: 'Create Roster',
    },
    auth: [AUTH_CATEGORIES.ADMIN],
  },
  CHECKOUT_PAGE: {
    path: '/checkout',
    tracking: {
      pageName: (userCategory) =>
        userCategory === AUTH_CATEGORIES.PROVIDER
          ? 'Single Checkout'
          : 'Group Checkout',
    },
    auth: [AUTH_CATEGORIES.ADMIN, AUTH_CATEGORIES.PROVIDER],
  },
  TERMS_PAGE: {
    path: '/terms',
    tracking: { pageName: 'Terms of Service' },
    // No auth - public page
  },
  FAQ_PAGE: {
    path: '/faq',
    tracking: { pageName: 'FAQs' },
    auth: [AUTH_CATEGORIES.ADMIN, AUTH_CATEGORIES.PROVIDER],
  },
  ERROR_PAGE: {
    path: '*',
    tracking: { pageName: 'Error Page' },
    // No auth - catch-all error page
  },
});

// Development check: warn if a route is missing tracking config
if (process.env.NODE_ENV !== 'production') {
  Object.entries(ROUTES).forEach(([routeName, routeConfig]) => {
    if (!routeConfig?.path) {
      console.error(
        `[Routing] Route "${routeName}" is missing a path definition!`,
      );
    }

    if (!routeConfig?.tracking) {
      console.error(
        `[Routing] Route "${routeName}" is missing tracking metadata!`,
      );
    }
  });
}
